import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Container, Cpu, FileDown, Radio, ScrollText, Search, SquareTerminal } from "lucide-react";
import { Logo } from "@/components/mdh/Nav";
import { scrollToId } from "@/components/mdh/primitives";
import AiAssistant from "@/components/mdh/AiAssistant";
import { DocSection, SourceBlocks } from "@/components/mdh/markdown";
import { API, BACKEND } from "@/lib/api";


const AGENT_DOWNLOADS = [
    {
        id: "agent-machine",
        icon: Cpu,
        title: "Machine agent",
        description: "CPU, memory, disk, processes — any Mac/Linux/Windows host",
        path: "/agent/script",
        file: "mdh_agent.py",
    },
    {
        id: "agent-ssh",
        icon: SquareTerminal,
        title: "SSH session agent",
        description: "Watches logins and command activity over SSH",
        path: "/agent/ssh-script",
        file: "mdh_ssh_agent.mjs",
    },
    {
        id: "agent-docker",
        icon: Container,
        title: "Docker agent",
        description: "Container starts, stops, health, and image drift",
        path: "/agent/docker-script",
        file: "mdh_docker_agent.mjs",
    },
    {
        id: "agent-log",
        icon: ScrollText,
        title: "Log watcher",
        description: "Tails any log file, ships matching lines as events",
        path: "/agent/log-script",
        file: "mdh_log_agent.mjs",
    },
    {
        id: "agent-listen",
        icon: Radio,
        title: "Stream CLI",
        description: "Tail any app's live event stream from your terminal",
        path: "/agent/listen-script",
        file: "mdh_listen.mjs",
    },
];


const SECTION_GROUPS = [
    {
        label: "Getting Started",
        items: [
            { id: "docs-intro", label: "Introduction" },
            { id: "docs-quickstart", label: "Quickstart" },
            { id: "docs-auth", label: "Authentication" },
        ],
    },
    {
        label: "Receiving",
        items: [
            { id: "docs-webhooks", label: "Webhook Receiver" },
            { id: "docs-signatures", label: "Signatures" },
            { id: "docs-retries", label: "Retries & Replay" },
        ],
    },
    {
        label: "Publishing",
        items: [
            { id: "docs-events", label: "Events API" },
            { id: "docs-endpoints", label: "Endpoints API" },
        ],
    },
    {
        label: "Monitoring",
        items: [
            { id: "docs-monitors", label: "Monitors & Agents" },
            { id: "docs-live", label: "Live Stream" },
        ],
    },
    {
        label: "Reference",
        items: [
            { id: "docs-rate-limits", label: "Rate Limits" },
            { id: "docs-downloads", label: "Downloads" },
        ],
    },
    {
        label: "Tools",
        items: [{ id: "assistant", label: "AI Assistant" }],
    },
];

export default function Docs() {
    const [assets, setAssets] = useState(null);
    const [sections, setSections] = useState(null);
    const [query, setQuery] = useState("");

    // searchable index: every section plus every ">> METHOD /path ::" endpoint line
    const searchIndex = useMemo(() => {
        const out = [];
        for (const s of sections || []) {
            out.push({ sectionId: s.id, label: s.title, desc: "section", sectionTitle: s.title });
            for (const line of (s.source || "").split("\n")) {
                const m = line.match(/^>>\s+(\S+)\s+(\S+)\s*::\s*(.*)$/);
                if (m)
                    out.push({
                        sectionId: s.id,
                        label: `${m[1]} ${m[2]}`,
                        desc: m[3],
                        sectionTitle: s.title,
                    });
            }
        }
        return out;
    }, [sections]);

    const q = query.trim().toLowerCase();
    const results = q
        ? searchIndex
              .filter((i) =>
                  `${i.label} ${i.desc} ${i.sectionTitle}`.toLowerCase().includes(q),
              )
              .slice(0, 8)
        : [];

    const jump = (sectionId) => {
        scrollToId(`#${sectionId}`);
        setQuery("");
    };

    useEffect(() => {
        fetch(`${API}/assets`)
            .then((r) => (r.ok ? r.json() : { assets: [] }))
            .then((d) => setAssets(d.assets || []))
            .catch(() => setAssets([]));
        fetch(`${API}/docs-sections`)
            .then((r) => (r.ok ? r.json() : { sections: [] }))
            .then((d) => setSections(d.sections || []))
            .catch(() => setSections([]));
    }, []);

    return (
        <div data-testid="docs-page" className="min-h-screen">
            <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800/80 bg-[#0B0F17]/92">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
                    <div className="flex items-center gap-3">
                        <Logo />
                        <span className="rounded border border-cyan-500/30 bg-cyan-950/50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-cyan-300">
                            Docs
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/"
                            data-testid="docs-back-link"
                            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-cyan-300"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
                        </Link>
                        <a
                            href="https://mdhapi.com/signup"
                            data-testid="docs-start-building-button"
                            className="hidden items-center gap-1.5 rounded-md bg-cyan-400 px-4 py-2 text-xs font-bold text-slate-950 shadow-[0_0_18px_rgba(6,182,212,0.35)] transition-colors hover:bg-cyan-300 sm:flex"
                        >
                            Start Building <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl px-5 pb-24 pt-28 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[200px_minmax(0,1fr)]">
                    <aside className="hidden lg:block">
                        <nav className="sticky top-24 space-y-1" aria-label="Docs">
                            <div className="relative mb-3">
                                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-600" />
                                <input
                                    data-testid="docs-search-input"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Jump to endpoint…"
                                    className="w-full rounded-md border border-slate-800 bg-slate-950/70 py-1.5 pl-7 pr-2 font-mono text-[10px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                                />
                                {q && (
                                    <div
                                        data-testid="docs-search-results"
                                        className="absolute inset-x-0 top-full z-20 mt-1 overflow-hidden rounded-md border border-slate-800 bg-[#0D131f] shadow-xl"
                                    >
                                        {results.length === 0 && (
                                            <p className="px-3 py-2.5 font-mono text-[10px] text-slate-600">
                                                no matches
                                            </p>
                                        )}
                                        {results.map((r, i) => (
                                            <button
                                                key={`${r.sectionId}-${r.label}-${i}`}
                                                data-testid={`docs-search-result-${i}`}
                                                onClick={() => jump(r.sectionId)}
                                                className="block w-full border-b border-slate-800/60 px-3 py-2 text-left last:border-0 hover:bg-slate-900"
                                            >
                                                <span className="block truncate font-mono text-[10.5px] text-cyan-300">
                                                    {r.label}
                                                </span>
                                                <span className="block truncate font-mono text-[9px] text-slate-500">
                                                    {r.sectionTitle}
                                                    {r.desc && r.desc !== "section" ? ` · ${r.desc}` : ""}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {SECTION_GROUPS.map((g) => (
                                <div key={g.label} className="pt-3 first:pt-0">
                                    <div
                                        data-testid={`docs-nav-group-${g.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="px-3 pb-1 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-600"
                                    >
                                        {g.label}
                                    </div>
                                    {g.items.map((s) => (
                                        <button
                                            key={s.id}
                                            data-testid={`docs-nav-${s.id}`}
                                            onClick={() => scrollToId(`#${s.id}`)}
                                            className="block w-full rounded-md px-3 py-1.5 text-left font-mono text-[11px] text-slate-500 transition-colors hover:bg-slate-900 hover:text-cyan-300"
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </nav>
                    </aside>

                    <div className="min-w-0">
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal-400/80">
                            MDH API Documentation
                        </p>
                        <h1
                            data-testid="docs-title"
                            className="font-hero mt-3 text-3xl font-bold tracking-tight text-slate-100 sm:text-4xl"
                        >
                            Build on the delivery engine.
                        </h1>
                        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400">
                            Everything you need to publish events, register endpoints, verify
                            signatures, and replay deliveries — plus downloadable specs and an AI
                            assistant that knows this API end to end.
                        </p>

                        <div className="mt-10">
                            {sections === null ? (
                                <p className="font-mono text-xs text-slate-500">
                                    Loading documentation…
                                </p>
                            ) : (
                                sections.map((s) => (
                                    <DocSection key={s.id} id={s.id} title={s.title}>
                                        <SourceBlocks source={s.source} />
                                    </DocSection>
                                ))
                            )}

                            <DocSection id="docs-downloads" title="Downloads">
                                <p>
                                    The agents and CLI are served live from the API — always the
                                    latest build. Specs and exports round out the toolchain.
                                </p>

                                <h3 className="!mt-6 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                                    Agents &amp; CLI
                                </h3>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                    {AGENT_DOWNLOADS.map((a) => (
                                        <a
                                            key={a.id}
                                            href={`${API}${a.path}`}
                                            download
                                            data-testid={`docs-download-${a.id}`}
                                            className="group flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-[#111827] p-4 transition-colors hover:border-cyan-500/40"
                                        >
                                            <div className="flex min-w-0 items-center gap-3">
                                                <a.icon className="h-4 w-4 shrink-0 text-cyan-400" />
                                                <div className="min-w-0">
                                                    <div className="text-xs font-semibold text-slate-200">
                                                        {a.title}
                                                    </div>
                                                    <div className="truncate font-mono text-[10px] text-slate-500">
                                                        {a.description}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="shrink-0 font-mono text-[10px] text-slate-600 transition-colors group-hover:text-cyan-300">
                                                {a.file}
                                            </span>
                                        </a>
                                    ))}
                                </div>

                                <h3 className="!mt-8 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                                    Specs &amp; exports
                                </h3>
                                <p className="!mt-2 font-mono text-[10px] text-slate-600">
                                    Tip: every app can export its own always-current OpenAPI spec
                                    and Postman collection from its dashboard → API Reference.
                                </p>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                    {assets === null ? (
                                        <p className="font-mono text-xs text-slate-500">
                                            Loading assets…
                                        </p>
                                    ) : assets.length === 0 ? (
                                        <p className="font-mono text-xs text-slate-500">
                                            No assets published yet.
                                        </p>
                                    ) : (
                                        assets.map((a) => (
                                            <a
                                                key={a.id}
                                                href={`${API}/files/${a.storage_path}`}
                                                download
                                                data-testid={`docs-download-${a.id}`}
                                                className="group flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-[#111827] p-4 transition-colors hover:border-cyan-500/40"
                                            >
                                                <div className="flex min-w-0 items-center gap-3">
                                                    <FileDown className="h-4 w-4 shrink-0 text-cyan-400" />
                                                    <div className="min-w-0">
                                                        <div className="text-xs font-semibold text-slate-200">
                                                            {a.title}
                                                        </div>
                                                        <div className="truncate font-mono text-[10px] text-slate-500">
                                                            {a.description}
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className="shrink-0 font-mono text-[10px] text-slate-500">
                                                    {Math.max(1, Math.round(a.size / 1024))} KB
                                                </span>
                                            </a>
                                        ))
                                    )}
                                </div>
                            </DocSection>

                            <DocSection id="assistant" title="AI Assistant">
                                <p>
                                    Ask questions about this API or describe an integration and get
                                    generated SDK code. Pick your model per question.
                                </p>
                                <AiAssistant />
                            </DocSection>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
